import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';

const PrivacyPolicy: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-28">
            <SEO
                title="Privacy Policy"
                description="Our commitment to your privacy. Learn how we collect, use, and protect your data at NexOverseas."
                canonical="/privacy-policy"
            />
            <div className="max-w-4xl mx-auto px-6 md:px-8 py-16">
                {/* Back Link */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[#009B8F] hover:text-[#007d72] transition-colors mb-8 font-medium"
                >
                    <ArrowLeft size={18} />
                    Back to Home
                </Link>

                {/* Header */}
                <div className="mb-12">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#00151a] mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-slate-500 text-sm">Last updated: 1st March 2026</p>
                </div>

                {/* Content */}
                <div className="prose prose-slate max-w-none">
                    <div className="space-y-6 text-slate-700 leading-relaxed">
                        <p>
                            This Privacy Policy explains how Nex Overseas collects, uses, and protects personal information when you use our website and services. Our platform provides education consultancy, coaching and training, accommodation support, travel assistance, and a solicitor‑connection service for immigration or legal matters. We do not provide immigration advice ourselves. Any legal or immigration‑related guidance is given directly by the solicitor or firm you choose to work with.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Account Information</h2>
                        <p>
                            When you create an account, we collect your name and email address. We do not store your password; it is encrypted and managed securely by our authentication provider. During free consultations or assessments, we may collect basic personal details or information you choose to enter on our website. This information is used only to provide your results and improve your experience. We do not ask you to upload documents during the free consultation stage.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Additional Services</h2>
                        <p>
                            If you purchase a subscription or request additional services, we may collect further information related to education, coaching, accommodation, travel, or solicitor‑connection services. This may include academic history, preferences, or other details you choose to share. We only collect this information with your permission and use it solely to deliver the services you request. You remain in control of what you share with us.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Payment Information</h2>
                        <p>
                            We do not store payment card details. All payments are processed securely through trusted third‑party providers such as Stripe or PayPal. We only keep basic transaction records required for accounting, fraud prevention, and legal compliance. If you request to be connected with a solicitor, we will only share your information with your explicit permission. We are not responsible for the advice, decisions, or actions of any solicitor or legal firm.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Technical Information</h2>
                        <p>
                            We may collect technical information automatically when you use our website, such as device details, browser type, IP address, and usage data. This helps us improve performance, maintain security, and understand how users interact with our platform. We may also use anonymized data to improve our internal systems. Anonymized data cannot identify you and may be kept indefinitely.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Nex AI Checker Extension</h2>
                        <p>
                            Our Nex AI Checker Chrome extension is designed to assist you in evaluating job postings for visa sponsorship and suitability.
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li><strong>Data Collection:</strong> When you use the extension, we collect job-related information (such as job title, company name, and job description) from the career pages you visit. We also use your NexOverseas account credentials to authenticate your requests.</li>
                            <li><strong>Data Usage:</strong> This information is sent to our secure backend servers to be processed by our AI engine. We use this data solely to provide you with a verdict on the job's suitability and any potential visa sponsorship information.</li>
                            <li><strong>Data Protection:</strong> We do not share your personal data or the job data collected by the extension with third parties for marketing or advertising purposes.</li>
                            <li><strong>Session Security:</strong> The extension communicates with our servers over encrypted HTTPS connections to ensure your data remains secure during transmission.</li>
                        </ul>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Your Rights</h2>
                        <p>
                            We do not sell your data or share it with third parties for advertising without your consent. You have the right to access your data, correct inaccurate information, request deletion, withdraw consent, or request a copy of your data. To exercise your rights, you can contact us at <a href="mailto:info@nexoverseas.co.uk" className="text-[#009B8F] hover:underline">info@nexoverseas.co.uk</a>.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Policy Updates</h2>
                        <p>
                            We may update this Privacy Policy from time to time. If changes are significant, we will notify you on our website or by email. By continuing to use our services after updates, you accept the revised Policy.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
